import React, { ReactNode, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import { Collapse } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import qs from 'qs'
import { useAppSelector } from 'store'
import styled, { css } from 'styled-components'

import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { useHandleSearchParams } from 'hooks/useHandleSearchParams'
import { selectBrands } from 'services/assetsSlice'
import { selectUserPermissions } from 'services/authSlice'
import { KEYS, localStorageService } from 'utils/localStorage'
import { PATHS } from 'utils/paths'
import { isNotNilOrEmpty } from 'utils/ramda'
import appTheme from 'utils/theme'

interface MenuProps {
  menuOpen: boolean
  overlay?: boolean
  onClose?: () => void
}

type TExtrapadding = '0' | '12' | '24' | '36' | '48' | '60' | '72'
type MenuMode = 'products' | 'landing-pages'

export const DEFAULT_PAGINATION = {
  size: 8,
  page: 1
}

export const Menu = ({ menuOpen, overlay = false, onClose }: MenuProps) => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { country } = useHandleSearchParams()
  const brands = useAppSelector(selectBrands)
  const localStorageMenu: string[] = (localStorageService.get(KEYS.menu) || '')
    .split(',')
    .filter(Boolean)

  const userPermissions = useAppSelector(selectUserPermissions)
  const isLandingPagesEnabled = useFeatureFlag('landingPages')

  const [activeMenuTab, setActiveMenuTab] = useState<string>('')
  const [menuMode, setMenuMode] = useState<MenuMode>(() => {
    const storedMode = localStorageService.get(KEYS.menuMode)

    return storedMode === 'landing-pages' ? 'landing-pages' : 'products'
  })

  useEffect(() => {
    if (pathname === PATHS.assets) {
      const searchIdName = qs.parse(search.replace('?', ''))

      if (searchIdName.brand && searchIdName.product) {
        setActiveMenuTab(`${searchIdName.brand}-${searchIdName.product}`)
      }
    } else if (pathname === PATHS.landingPagesAssets) {
      const searchIdName = qs.parse(search.replace('?', ''))

      if (searchIdName.brand && searchIdName.product) {
        setActiveMenuTab(
          `landing-pages-assets-${searchIdName.brand}-${searchIdName.product}`
        )
      } else {
        setActiveMenuTab('landing-pages-assets')
      }
    } else if (pathname === PATHS.products) {
      setActiveMenuTab('products')
    } else if (pathname === PATHS.landingPages) {
      setActiveMenuTab('landing-pages')
    } else if (pathname === PATHS.landingPagesPackages) {
      setActiveMenuTab('landing-pages-packages')
    } else if (pathname === PATHS.landingPagesIframes) {
      setActiveMenuTab('landing-pages-iframes')
    } else if (
      pathname === PATHS.reviews ||
      pathname.startsWith(`${PATHS.reviews}/`)
    ) {
      setActiveMenuTab('reviews')
    } else {
      setActiveMenuTab(pathname.split('/')[1])
    }
     
  }, [pathname, search])

  const [whichCollapseIsOpen, setWhichCollapseIsOpen] =
    useState<string[]>(localStorageMenu)

  const brandNames = (brands ?? []).map(b => b.name)

  const toggleExpanded = (id: string) =>
    setWhichCollapseIsOpen((prev: string[]) => {
      const isOpening = !prev.includes(id)
      let next = [...prev]

      if (isOpening) {
        // Brand level: only one brand open at a time (under Products or LP Assets)
        if (brandNames.includes(id)) {
          next = next.filter(x => !brandNames.includes(x))
        }
        next = next.includes(id) ? next : [...next, id]
      } else {
        next = next.filter(item => item !== id)
      }

      localStorageService.set(KEYS.menu, next.join(','))

      return next
    })

  const handleMenuModeChange = (mode: MenuMode) => {
    if (mode === menuMode) return
    setMenuMode(mode)
    localStorageService.set(KEYS.menuMode, mode)
    navigate(mode === 'products' ? `${PATHS.products}?country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}&brand=All` : `${PATHS.landingPages}?country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}&brand=All`)
  }

  useEffect(() => {
    if (!isLandingPagesEnabled && menuMode === 'landing-pages') {
      setMenuMode('products')
      localStorageService.set(KEYS.menuMode, 'products')
    }
  }, [isLandingPagesEnabled, menuMode])

  const navigateToAssets = (brandProductPath: string) => {
    return navigate(
      `${PATHS.assets}?${brandProductPath}&asset=all&country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}`
    )
  }

  const navigateToLandingPagesAssets = (brandProductPath: string) => {
    return navigate(
      `${PATHS.landingPagesAssets}?${brandProductPath}&asset=all&country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}`
    )
  }

  type ColapseRowMenuProps = {
    children: ReactNode
    id: string
    bold?: boolean
    firstMenuStep?: boolean
    extrapadding: TExtrapadding
    customText?: string
    $active?: string
    /** Nesting level for active background shade: 0 = lightest (top accordion), 1/2 = nested, leaf uses menuActive. */
    $activeLevel?: 0 | 1 | 2
  }

  const ColapseRowMenu = ({
    children,
    id,
    bold,
    firstMenuStep,
    extrapadding,
    customText,
    $active,
    $activeLevel = 0
  }: ColapseRowMenuProps) => {
    return (
      <>
        <ColapseButton
          onClick={() => toggleExpanded(id)}
          $bold={bold}
          $firstMenuStep={firstMenuStep}
          $extrapadding={extrapadding}
          $active={$active}
          $activeLevel={$activeLevel}
          id={id}
        >
          {customText ?? id}
          <ArrowDown $open={whichCollapseIsOpen.includes(id) ? 1 : 0} />
        </ColapseButton>
        <ColapseContent $extrapadding={extrapadding}>
          <Collapse in={whichCollapseIsOpen.includes(id)}>{children}</Collapse>
        </ColapseContent>
      </>
    )
  }

  const navOffset = appTheme.dimensions.navBarHeight
  const belowNavHeight = `calc(100vh - ${navOffset}px)`

  return (
    <StyledDrawer
      variant={overlay ? 'temporary' : 'persistent'}
      anchor='left'
      open={menuOpen}
      onClose={overlay ? onClose : undefined}
      $overlay={overlay}
      PaperProps={
        overlay
          ? {
              sx: {
                width: 300,
                top: `${navOffset}px`,
                height: belowNavHeight,
                maxHeight: belowNavHeight,
                boxSizing: 'border-box',
                boxShadow: appTheme.shadows.menu,
                border: 'none'
              }
            }
          : undefined
      }
      BackdropProps={
        overlay
          ? {
              sx: {
                top: `${navOffset}px`,
                height: belowNavHeight
              }
            }
          : undefined
      }
      ModalProps={overlay ? { keepMounted: true } : undefined}
    >
      <Wrapper $overlay={overlay}>
        <MenuItems>
          {menuMode === 'products' ? (
            <>
              {userPermissions?.can_view_product_list ? (
                <RowMenu
                  id='products'
                  $active={activeMenuTab}
                  onClick={() =>
                    navigate(
                      `${PATHS.products}?country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}&brand=All`
                    )
                  }
                  $bold={1}
                  $firstMenuStep
                  $extrapadding='12'
                >
                  Products
                </RowMenu>
              ) : null}
              {userPermissions?.can_view_asset_list ? (
                <ColapseRowMenu
                  id='assets'
                  bold={true}
                  firstMenuStep={true}
                  extrapadding='24'
                  customText='Assets'
                  $active={activeMenuTab}
                  $activeLevel={1}
                >
                  {isNotNilOrEmpty(brands)
                    ? brands.map(brand => (
                        <ColapseRowMenu
                          key={brand.id}
                          id={brand.name}
                          bold={true}
                          extrapadding='36'
                          $active={activeMenuTab}
                          $activeLevel={2}
                        >
                          {isNotNilOrEmpty(brand.productCategories)
                            ? brand.productCategories.map(productCategorie => (
                                <RowMenu
                                  key={productCategorie.id}
                                  id={`${brand.name}-${productCategorie.name}`}
                                  $active={activeMenuTab}
                                  onClick={() =>
                                    navigateToAssets(
                                      `brand=${brand.name}&product=${productCategorie.name}`
                                    )
                                  }
                                  $extrapadding='72'
                                >
                                  {productCategorie.name}
                                </RowMenu>
                              ))
                            : null}
                        </ColapseRowMenu>
                      ))
                    : null}
                </ColapseRowMenu>
              ) : null}
              {userPermissions?.can_view_asset_list ? (
                <RowMenu
                  id='assets-search'
                  $active={activeMenuTab}
                  onClick={() =>
                    navigate(
                      `${PATHS.assetsSearch}?brand=All&country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}`
                    )
                  }
                  $extrapadding='24'
                  $isAccordionButtonStyle
                >
                  Assets search
                </RowMenu>
              ) : null}
              {userPermissions?.can_view_package_list ? (
                <RowMenu
                  id='packages'
                  $active={activeMenuTab}
                  onClick={() =>
                    navigate(
                      `${PATHS.packages}?size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}`
                    )
                  }
                  $extrapadding='24'
                  $isAccordionButtonStyle
                >
                  Packages
                </RowMenu>
              ) : null}
              {userPermissions?.can_view_package_list ? (
                <RowMenu
                  id='iframes'
                  $active={activeMenuTab}
                  onClick={() =>
                    navigate(
                      `${PATHS.iframes}?size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}`
                    )
                  }
                  $extrapadding='24'
                  $isAccordionButtonStyle
                >
                  Iframes
                </RowMenu>
              ) : null}
              {userPermissions?.can_view_review_session ? (
                <RowMenu
                  id='reviews'
                  $active={activeMenuTab}
                  onClick={() =>
                    navigate(
                      `${PATHS.reviewsProducts}?size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}`
                    )
                  }
                  $extrapadding='24'
                  $isAccordionButtonStyle
                >
                  Reviews
                </RowMenu>
              ) : null}
            </>
          ) : (
            <>
              <RowMenu
                id='landing-pages'
                $active={activeMenuTab}
                onClick={() =>
                  navigate(
                    `${PATHS.landingPages}?country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}&brand=All`
                  )
                }
                $bold={1}
                $firstMenuStep
                $extrapadding='12'
              >
                Landing Pages
              </RowMenu>
              {userPermissions?.can_view_asset_list ? (
                <ColapseRowMenu
                  id='landing-pages-assets'
                  bold={true}
                  firstMenuStep={true}
                  extrapadding='24'
                  customText='Assets'
                  $active={activeMenuTab}
                  $activeLevel={1}
                >
                  {isNotNilOrEmpty(brands)
                    ? brands.map(brand => (
                        <ColapseRowMenu
                          key={brand.id}
                          id={`${brand.name}`}
                          bold={true}
                          extrapadding='36'
                          $active={activeMenuTab}
                          $activeLevel={2}
                        >
                          {isNotNilOrEmpty(brand.productCategories)
                            ? brand.productCategories.map(productCategorie => (
                                <RowMenu
                                  key={productCategorie.id}
                                  id={`landing-pages-assets-${brand.name}-${productCategorie.name}`}
                                  $active={activeMenuTab}
                                  onClick={() =>
                                    navigateToLandingPagesAssets(
                                      `brand=${brand.name}&product=${productCategorie.name}`
                                    )
                                  }
                                  $extrapadding='72'
                                >
                                  {productCategorie.name}
                                </RowMenu>
                              ))
                            : null}
                        </ColapseRowMenu>
                      ))
                    : null}
                </ColapseRowMenu>
              ) : null}
              <RowMenu
                id='landing-pages-packages'
                $active={activeMenuTab}
                onClick={() => navigate(PATHS.landingPagesPackages)}
                $extrapadding='24'
                $isAccordionButtonStyle
              >
                Packages
              </RowMenu>
              <RowMenu
                id='landing-pages-iframes'
                $active={activeMenuTab}
                onClick={() => navigate(PATHS.landingPagesIframes)}
                $extrapadding='24'
                $isAccordionButtonStyle
              >
                Iframes
              </RowMenu>
              {userPermissions?.can_view_review_session ? (
                <RowMenu
                  id='reviews'
                  $active={activeMenuTab}
                  onClick={() =>
                    navigate(
                      `${PATHS.reviewsLandingPages}?size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}`
                    )
                  }
                  $extrapadding='24'
                  $isAccordionButtonStyle
                >
                  Reviews
                </RowMenu>
              ) : null}
            </>
          )}
          <RowMenu
            id='image-editor'
            $active={activeMenuTab}
            onClick={() => navigate(PATHS.imageEditor)}
            $bold={1}
            $firstMenuStep
            $extrapadding='12'
          >
            Image Editor
          </RowMenu>
        </MenuItems>
        <MenuModeToggle>
          <MenuModeButton
            $selected={menuMode === 'products' ? 1 : 0}
            onClick={() => handleMenuModeChange('products')}
          >
            Products
          </MenuModeButton>
          <MenuModeButton
            $selected={menuMode === 'landing-pages' ? 1 : 0}
            onClick={() => handleMenuModeChange('landing-pages')}
            disabled={!isLandingPagesEnabled}
            title={!isLandingPagesEnabled ? 'Landing Pages feature is disabled' : undefined}
          >
            Landing Pages
          </MenuModeButton>
        </MenuModeToggle>
      </Wrapper>
    </StyledDrawer>
  )
}

const Wrapper = styled.div<{ $overlay?: boolean }>`
  padding: 20px 20px 14px;
  height: ${({ $overlay }) =>
    $overlay ? '100%' : `calc(100vh - ${appTheme.dimensions.navBarHeight}px)`};
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ $overlay, theme }) =>
    $overlay ? 'none' : theme.shadows.menu};
`

const MenuItems = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-right: -6px;
  padding-right: 6px;
`

const MenuModeToggle = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 10px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.menuHover};
`

const MenuModeButton = styled.button<{ $selected: 0 | 1 }>`
width: 50%;
  border: 0;
  border-radius: 5px;
  padding: 7px 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.white : theme.colors.primary};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : 'transparent'};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const RowMenu = styled.div<{
  $bold?: 0 | 1
  id: string
  $active: string
  $firstMenuStep?: boolean
  $extrapadding: TExtrapadding
  $isAccordionChild?: boolean
  $isAccordionButtonStyle?: boolean
}>`
  font-size: ${({ $isAccordionChild, $isAccordionButtonStyle }) => 
    ($isAccordionChild || $isAccordionButtonStyle) ? '15px' : '16px'};
  font-weight: 400;
  line-height: ${({ $isAccordionChild, $isAccordionButtonStyle }) => 
    ($isAccordionChild || $isAccordionButtonStyle) ? '22px' : '24px'};
  text-align: left;
  padding: 12px;
  padding-left: ${({ $extrapadding, $isAccordionButtonStyle }) => 
    $isAccordionButtonStyle ? '24px' : `${$extrapadding}px`};
  cursor: pointer;
  font-weight: ${({ $bold, $isAccordionChild, $isAccordionButtonStyle }) => 
    $bold ? 'bold' : ($isAccordionButtonStyle ? 'bold' : ($isAccordionChild ? '500' : 'normal'))};
  color: ${({ theme, $firstMenuStep, $isAccordionChild, $isAccordionButtonStyle }) => {
    if ($isAccordionButtonStyle) {
      return theme.colors.primary
    }

    if ($firstMenuStep) {
      return theme.colors.primary
    }

    return $isAccordionChild ? theme.colors.menuLabel : theme.colors.menuLabel
  }};
  /* Only lowest level (selected row) gets active background. LP assets leaf has id e.g. candy-dishwasher while $active is landing-pages-assets-candy-dishwasher. */
  background-color: ${({ id, $active, theme }) => {
    if (!id || !$active) return 'unset';
    if (id === $active) return theme.colors.menuActive;

    return 'unset';
  }};

  &:hover {
    background-color: ${({ theme }) => theme.colors.menuHover};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.menuActive};
  }
`
const ArrowDown = styled(ChevronRightOutlinedIcon) <{ $open: 0 | 1 }>`
  transition: all 0.3s;
  transform: rotate(90deg);

  ${({ $open }) =>
    $open &&
    css`
      transform: rotate(270deg);
    `};
`
const ColapseButton = styled(RowMenu) <{
  id?: string
  $active?: string
  $activeLevel?: 0 | 1 | 2
  $bold?: boolean
  $firstMenuStep?: boolean
  $extrapadding: TExtrapadding
}>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-weight: ${({ $bold }) => ($bold ? 'bold' : 'normal')};
  font-size: ${({ $extrapadding }) => ($extrapadding === '24') ? '15px' : '16px'};
  line-height: ${({ $extrapadding }) => ($extrapadding === '24') ? '22px' : '24px'};
  background-color: unset;
  color: ${({ theme, $firstMenuStep }) =>
    $firstMenuStep ? theme.colors.primary : theme.colors.menuLabel};
`
const ColapseContent = styled.div<{
  $extrapadding: TExtrapadding
}>`
  display: flex;
  width: 100%;
  // padding-left: ${({ $extrapadding }) => ($extrapadding ? '24px' : '12px')};

  .MuiCollapse-root {
    width: 100%;
  }
`
const StyledDrawer = styled(Drawer)<{ $overlay?: boolean }>`
  ${({ $overlay }) =>
    $overlay &&
    css`
      &.MuiDrawer-root {
        width: 0 !important;
      }
    `}
`
